import OriginalPhoneField, { PhoneInputProps } from "react-native-phone-number-input";
import parsePhoneNumber from "libphonenumber-js";
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { StyleSheet, Platform } from "react-native";
import BaseField, { BaseFieldProps } from "./BaseField";
import { useTheme } from "../hooks/use-theme";

type PhoneFieldProps = Omit<BaseFieldProps, "component"> &
    PhoneInputProps & {
        initialValue?: string;
        onPhoneNumberChanged?: (number: string) => void;
        onBlur?: () => Promise<void> | void;
        onSubmitEditing?: () => void;
        isValid?: boolean;
    };

export type PhoneFieldHandle = {
    isValidNumber: (num: string) => boolean;
    formatPhoneNumber: () => void;
};

// eslint-disable-next-line react/display-name
const PhoneField = forwardRef<PhoneFieldHandle, PhoneFieldProps>(
    (
        {
            initialValue,
            onPhoneNumberChanged,
            style,
            isValid = true,
            disabled,
            label,
            placeholder,
            value,
            error,
            onFocus: propsOnFocus,
            onBlur: propsOnBlur,
            ...props
        },
        forwardedRef,
    ) => {
        const [isFocused, setIsFocused] = useState(false);
        const styles = useStyles();
        const phoneInputRef = useRef<OriginalPhoneField>(null);
        const isValidNumber = (arg: string): boolean => {
            return !!phoneInputRef.current?.isValidNumber(arg);
        };

        const formatPhoneNumber = useCallback(() => {
            const inputValue =
                phoneInputRef.current
                    ?.getNumberAfterPossiblyEliminatingZero()
                    .formattedNumber.replaceAll(" ", "") || "";

            const parsed = parsePhoneNumber(inputValue || "");
            if (parsed) {
                phoneInputRef.current?.setState({
                    number: parsed?.nationalNumber,
                    countryCode: parsed?.country as never,
                    code: parsed?.countryCallingCode,
                });
            }
            return inputValue;
        }, []);

        useImperativeHandle(forwardedRef, () => ({ isValidNumber, formatPhoneNumber }));

        const initialPhoneNumber = useMemo(() => {
            return parsePhoneNumber(initialValue || "");
        }, [initialValue]);

        useEffect(() => {
            let inputValue = value || "";

            if (inputValue && !inputValue.startsWith("+")) {
                inputValue = `+${phoneInputRef.current?.getCallingCode()}${inputValue}`;
            }

            const parsed = parsePhoneNumber(inputValue);
            if (parsed) {
                phoneInputRef.current?.setState({
                    number: parsed?.nationalNumber,
                    countryCode: parsed?.country as never,
                    code: parsed?.countryCallingCode,
                });
            }
        }, [value]);

        const onFocus = useCallback(() => {
            setIsFocused(true);
            propsOnFocus?.();
        }, [propsOnFocus]);

        const onBlur = useCallback(() => {
            setIsFocused(false);
            propsOnBlur?.();
        }, [propsOnBlur]);

        const fieldId = useMemo(() => uuidv4(), []);

        // autoFocus prop doesn't natively work on react-native-phone-number-input on web
        useEffect(() => {
            if (Platform.OS === "web" && props.autoFocus) {
                document.getElementById(fieldId)?.focus?.();
            }

            // Only run on mount
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const { onSubmitEditing, ...phoneInputProps } = props;

        return (
            <BaseField
                style={style}
                labelLeftPadding={106}
                value={!!value}
                disabled={disabled}
                label={label}
                placeholder={placeholder}
                onFocus={onFocus}
                onBlur={onBlur}
                isFocused={isFocused}
                error={error}
                componentRef={phoneInputRef}
                component={
                    <OriginalPhoneField
                        defaultValue={initialPhoneNumber?.nationalNumber}
                        defaultCode={(initialPhoneNumber?.country as never) || "CH"}
                        layout="first"
                        onChangeFormattedText={(newValue) => {
                            onPhoneNumberChanged?.(newValue);
                        }}
                        textContainerStyle={styles.textContainer}
                        textInputStyle={[disabled && styles.textInputDisabled]}
                        codeTextStyle={[styles.codeText, disabled && styles.textInputDisabled]}
                        flagButtonStyle={{
                            width: "auto",
                            height: Platform.OS === "web" ? 20 : 28,
                        }}
                        placeholder=" "
                        containerStyle={[styles.container]}
                        countryPickerProps={{
                            // explicitly specify this to avoid error when disabled in Scrollview.
                            // @see https://github.com/garganurag893/react-native-phone-number-input/issues/117
                            disableNativeModal: false,
                        }}
                        textInputProps={{
                            id: fieldId,
                            //eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            //@ts-ignore
                            selectionColor: null,
                            onSubmitEditing,
                            keyboardType: "number-pad",
                            keyboardAppearance: "light",
                            returnKeyType: isValid ? "done" : undefined,
                            onFocus,
                            onBlur,
                        }}
                        disabled={disabled}
                        {...phoneInputProps}
                    />
                }
            />
        );
    },
); 

const useStyles = () => {
    const { colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    alignItems: "center",
                    backgroundColor: "transparent",
                    width: "100%",
                    height: "100%",
                    padding: 0,
                },
                textContainer: {
                    backgroundColor: "transparent",
                    paddingVertical: 0,
                    paddingHorizontal: 0,
                },
                textInputDisabled: {
                    color: colors.backdrop,
                },
                codeText: {
                    width: 40,
                    ...fonts.regular,
                },
            }),
        [colors.backdrop, fonts.regular],
    );
};

export default PhoneField;
